import { useContext, useState } from "react";
import { ScrollBar } from "../../../Theme/Hyper";
import { APIContext } from "../../../contexts/APIContext";
import styled from "styled-components";
import { DataContext } from "../../../contexts/DataContext";
import {
  buildChampionsCupKnockout,
  buildChallengeCupKnockout,
  toSeedMap,
} from "../../../utils/epcrRanking";

const GraphicButton = styled.div`
  display: flex;
  align-items: center;
  background-color: ${(props) =>
    props.green ? "#0adb93" : props.red ? "#db0a41" : "#181d25"};
  color: ${(props) => (props.green ? "#1d1d1d" : "")};
  min-height: 40px;
  width: 90%;
  box-sizing: border-box;
  border-radius: 4px;
  margin-top: 1em;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  :hover {
    background-color: ${(props) =>
      props.green ? "#0adb93" : props.red ? "#db0a41" : "#db0a41"};
  }
`;

const GraphicsList = styled(ScrollBar)`
  display: flex;
  flex-direction: column;
  max-height: 560px;
  overflow-y: auto;
  width: 100%;
  align-items: center;
`;
const Row = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-evenly;
`;

export default function EPCRStandings({ previewGraphic, game }) {
  const { getTeams, getCompetitionByName } = useContext(APIContext);
  const { getStandings } = useContext(DataContext);
  const [mini, setMini] = useState(false);

  // Seeds every team 1..N following the official EPCR Round of 16
  // qualification order. For the Challenge Cup this also pulls in the
  // Investec Champions Cup's own standings, since the 4 ICC teams ranked
  // 17-20 there take fixed seeds 9-12 in the Challenge Cup picture.
  async function getSeeds(groups) {
    if (game?.competition?.name === "Champions Cup") {
      let knockout = buildChampionsCupKnockout(groups);
      return { seeds: toSeedMap(knockout), dropdowns: [] };
    }

    let iccCompetition = await getCompetitionByName({ name: "Champions Cup" });
    let iccData = iccCompetition
      ? await getStandings({
          compId: iccCompetition.oval_ID,
          seasonId: game?.season?.oval_ID,
        })
      : null;
    let { dropdownsToChallengeCup } = buildChampionsCupKnockout(
      iccData?.groups
    );
    let knockout = buildChallengeCupKnockout(groups, dropdownsToChallengeCup);
    return { seeds: toSeedMap(knockout), dropdowns: dropdownsToChallengeCup };
  }

  async function previewPoolOverview() {
    let teams = await getTeams({});
    let data = await getStandings({
      compId: game?.competition?.oval_ID,
      seasonId: game?.season?.oval_ID,
    });
    let table = { data: data };
    let { seeds } = await getSeeds(table?.data?.groups);
    let poolNames = [
      ...new Set(table?.data?.groups?.map((g) => g?.name)),
    ].sort();

    previewGraphic("Pool Overview", {
      title: "POOLS",
      pools: poolNames.map((poolName) => ({
        name: poolName,
        teams: table?.data?.groups
          ?.filter((d) => d.name === poolName)
          ?.sort((a, b) => {
            return a?.position - b?.position;
          })
          ?.map((row) => {
            return {
              ...row,
              teamName: row?.team?.name,
              P: row?.played || 0,
              W: row?.won || 0,
              L: row?.lost || 0,
              D: row?.drawn || 0,
              DIFF: row?.pointsDiff || 0,
              PTS: row?.points || 0,
              TOTALPOS: seeds.get(row?.team?.id) || row?.position,
            };
          }),
      })),
      teams,
      mini,
    });
  }

  async function previewOverallRanking() {
    let teams = await getTeams({});
    let data = await getStandings({
      compId: game?.competition?.oval_ID,
      seasonId: game?.season?.oval_ID,
    });
    let table = { data: data };
    let { seeds, dropdowns } = await getSeeds(table?.data?.groups);

    let ownTeams = (table?.data?.groups || []).map((row) => {
      return {
        ...row,
        teamName: row?.team?.name,
        poolName: row?.name,
        P: row?.played || 0,
        W: row?.won || 0,
        L: row?.lost || 0,
        D: row?.drawn || 0,
        DIFF: row?.pointsDiff || 0,
        PTS: row?.points || 0,
        TOTALPOS: seeds.get(row?.team?.id) || row?.position,
      };
    });

    let droppedDownTeams = (dropdowns || []).map((row) => {
      return {
        ...row,
        teamName: row?.team?.name,
        poolName: "ICC Wildcard",
        P: row?.played || 0,
        W: row?.won || 0,
        L: row?.lost || 0,
        D: row?.drawn || 0,
        DIFF: row?.pointsDiff || 0,
        PTS: row?.points || 0,
        TOTALPOS: seeds.get(row?.team?.id),
      };
    });

    previewGraphic("Overall Ranking", {
      title: "OVERALL RANKING",
      data: [...ownTeams, ...droppedDownTeams].sort(
        (a, b) => a.TOTALPOS - b.TOTALPOS
      ),
      teams,
      mini,
    });
  }

  async function previewPool(poolName) {
    let teams = await getTeams({});
    let data = await getStandings({
      compId: game?.competition?.oval_ID,
      seasonId: game?.season?.oval_ID,
    });
    let table = { data: data };
    let { seeds } = await getSeeds(table?.data?.groups);

    previewGraphic("Standings", {
      title: poolName.toUpperCase(),
      data: table?.data?.groups
        ?.filter((d) => d.name === poolName)
        ?.sort((a, b) => {
          return a?.position - b?.position;
        })
        ?.map((row) => {
          return {
            ...row,
            teamName: row?.team?.name,
            P: row?.played || 0,
            W: row?.won || 0,
            L: row?.lost || 0,
            D: row?.drawn || 0,
            DIFF: row?.pointsDiff || 0,
            PTS: row?.points || 0,
            TOTALPOS: seeds.get(row?.team?.id) || row?.position,
          };
        }),
      teams,
      mini,
    });
  }

  return (
    <div style={{ width: "100%" }}>
      <Row>
        <GraphicButton
          style={{ width: "45%" }}
          green={mini === false}
          onClick={() => {
            setMini(false);
          }}
        >
          FF
        </GraphicButton>
        <GraphicButton
          style={{ width: "45%" }}
          green={mini === true}
          onClick={() => {
            setMini(true);
          }}
        >
          Mini
        </GraphicButton>
      </Row>
      <GraphicsList>
        <GraphicButton onClick={previewPoolOverview}>
          Pool Overview
        </GraphicButton>
        <GraphicButton onClick={previewOverallRanking}>
          Overall Ranking
        </GraphicButton>
        <GraphicButton onClick={() => previewPool("Pool 1")}>
          Pool 1
        </GraphicButton>
        <GraphicButton onClick={() => previewPool("Pool 2")}>
          Pool 2
        </GraphicButton>
        <GraphicButton onClick={() => previewPool("Pool 3")}>
          Pool 3
        </GraphicButton>
        <GraphicButton onClick={() => previewPool("Pool 4")}>
          Pool 4
        </GraphicButton>
      </GraphicsList>
    </div>
  );
}
